import { NestedNavigationLinksType } from "common/SiteLinks"
import { trackMenuClicks } from "common/analytics"
import NextLink from "common/bit/NextLink"
import { closeCollapse, openCollapse } from "layout/oldLayout/CollapseLayout"
import { useRouter } from "next/router"
import { Fragment, useEffect, useState } from "react"
import ButtonBaseBlank from "reusable/ButtonBaseBlank"
import { useAppDispatch } from "store"
import styled from "styled-components"
import colors from "theme/colors"
import { StyledRow, TypographyCenter, TypographyRtl } from "theme/shared"
import ShopAuthLink, { StyledTypographyCenter } from "./ShopAuthLink"
import SignOutDialog from "./SignOutDialog"

const StyledLinkButton = styled(ButtonBaseBlank)<{ $selected?: boolean }>`
  display: flex;
  justify-content: center;
  cursor: pointer;
  p {
    color: ${(p) =>
      p.$selected ? p.theme.colors.white : p.theme.colors.borderGrey};
    font-weight: ${(p) => (p.$selected ? "bold" : "normal")};
  }
  &:hover {
    p {
      text-decoration: underline;
    }
  }
`

type HeaderLinkListProps = {
  linkList: NestedNavigationLinksType[]
  setSubLinks: any
  isShopHeader: boolean
  closeDrawer?: () => void
}

const HeaderLinkList = ({
  linkList,
  setSubLinks,
  isShopHeader = false,
}: HeaderLinkListProps) => {
  const [selectedLink, setSelectedLink] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const dispatch = useAppDispatch()
  const router = useRouter()

  useEffect(() => {
    const onRouteChange = () => {
      setSubLinks(null)
      setSelectedLink(null)
      dispatch(closeCollapse())
    }
    router.events.on("routeChangeComplete", onRouteChange)
    return () => {
      router.events.off("routeChangeComplete", onRouteChange)
    }
  }, [])

  return (
    <StyledRow
      $gap={isShopHeader ? "10px" : "10px 16px"}
      $notCenter
      $fullWidth
      alignItems="center"
      $wrap
    >
      <SignOutDialog dialogOpen={dialogOpen} setDialogOpen={setDialogOpen} />
      {linkList.map((item, index) => (
        <Fragment key={index}>
          {item.links.length === 1 ? (
            <NextLink
              href={item.links[0].link}
              openInNewTab={item.links[0].blank}
            >
              <StyledTypographyCenter
                variant="body1"
                $color={colors.borderGrey}
                onClick={() => {
                  setSubLinks(null)
                  setSelectedLink(item.title)
                  trackMenuClicks()
                }}
              >
                {item.title}
              </StyledTypographyCenter>
            </NextLink>
          ) : (
            <>
              {!item.links.length && isShopHeader ? (
                <ShopAuthLink
                  item={item}
                  selectedLink={selectedLink}
                  setDialogOpen={setDialogOpen}
                />
              ) : (
                <StyledLinkButton
                  $selected={item.title === selectedLink}
                  onClick={() => {
                    if (item.title === selectedLink) {
                      setSubLinks(null)
                      setSelectedLink(null)
                      dispatch(closeCollapse())
                    } else {
                      setSubLinks(item.links)
                      setSelectedLink(item.title)
                      dispatch(openCollapse())
                    }
                  }}
                  aria-expanded={item.title === selectedLink ? true : false}
                >
                  <TypographyCenter
                    variant="body1"
                    $color={colors.borderGrey}
                    onClick={() => {
                      trackMenuClicks()
                    }}
                  >
                    {item.title}
                  </TypographyCenter>
                </StyledLinkButton>
              )}
            </>
          )}
          {index !== linkList.length - 1 && (
            <TypographyRtl variant="body1" $color={colors.darkGrey}>
              |
            </TypographyRtl>
          )}
        </Fragment>
      ))}
    </StyledRow>
  )
}

export default HeaderLinkList
